import React, { useState } from 'react';
import {
  CheckCircle,
  XCircle,
  Loader,
  Ban,
  ChevronRight,
  ExternalLink,
  ListChecks,
} from 'lucide-react';
import { LoadingSpinner } from '../common';
import { formatTaskClass } from './utils';
import { Pagination } from './Pagination';
import type { TaskHistoryItem } from './types';

const PAGE_SIZE = 10;

interface TaskHistoryProps {
  history: TaskHistoryItem[];
  loading: boolean;
}

export const TaskHistory: React.FC<TaskHistoryProps> = ({ history, loading }) => {
  const [currentPage, setCurrentPage] = useState(1);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <LoadingSpinner size="md" />
      </div>
    );
  }

  if (history.length === 0) return null;

  const totalPages = Math.ceil(history.length / PAGE_SIZE);
  const visible = history.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  return (
    <section>
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 rounded-lg bg-teal-100 dark:bg-teal-900/30">
          <ListChecks className="w-5 h-5 text-teal-600 dark:text-teal-400" />
        </div>
        <h2 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>
          Past Actions
        </h2>
      </div>
      <div className="bg-white dark:bg-gray-800 border border-slate-200 dark:border-slate-700/50 rounded-xl divide-y divide-slate-200 dark:divide-slate-700/50">
        {visible.map((item: TaskHistoryItem) => {
          const isCompleted = item.status === 'completed';
          const isFailed = item.status === 'failed';
          const isRunning = item.status === 'running' || item.status === 'in_progress';
          const displayDate = item.completedAt || item.startedAt;

          return (
            <a
              key={item.id}
              href={`/tasks/${item.id}`}
              className="w-full text-left p-4 first:rounded-t-xl last:rounded-b-xl block transition-all duration-150 hover:-translate-y-0.5 hover:shadow-[0_2px_8px_rgba(20,184,166,0.15)] hover:border-teal-200 dark:hover:border-teal-800 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-offset-1"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {isCompleted && <CheckCircle className="w-5 h-5 text-emerald-500 shrink-0" />}
                  {isFailed && <XCircle className="w-5 h-5 text-red-500 shrink-0" />}
                  {isRunning && <Loader className="w-5 h-5 text-blue-500 animate-spin shrink-0" />}
                  {!isCompleted && !isFailed && !isRunning && (
                    <Ban className="w-5 h-5 text-gray-400 shrink-0" />
                  )}

                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium" style={{ color: 'var(--color-text-primary)' }}>
                        {formatTaskClass(item.taskClass)}
                      </span>
                      <span
                        className={`px-2 py-0.5 text-xs font-medium rounded capitalize ${
                          isCompleted
                            ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
                            : isFailed
                              ? 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                              : isRunning
                                ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                                : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
                        }`}
                      >
                        {item.status}
                      </span>
                    </div>
                    {isCompleted && item.prUrl && (
                      <span
                        className="inline-flex items-center gap-1 text-sm text-teal-600 dark:text-teal-400 hover:underline mt-0.5"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          window.open(item.prUrl!, '_blank', 'noopener,noreferrer');
                        }}
                      >
                        <ExternalLink className="w-3.5 h-3.5" />
                        View PR
                      </span>
                    )}
                    {isFailed && item.error && (
                      <p className="text-sm text-red-600 dark:text-red-400 mt-0.5 truncate max-w-md">
                        {item.error}
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-3 shrink-0">
                  {displayDate && (
                    <span className="text-sm" style={{ color: 'var(--color-text-tertiary)' }}>
                      {new Date(displayDate).toLocaleDateString(undefined, {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                    </span>
                  )}
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                </div>
              </div>
            </a>
          );
        })}
      </div>
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={history.length}
        pageSize={PAGE_SIZE}
        onPageChange={setCurrentPage}
      />
    </section>
  );
};
